import { Params } from '@angular/router';

import { AR_ROUTE } from 'app/ar/ar-routing.constants';
import { EXPLORE_ACTIVITIES_ROUTE } from 'app/explore-activities/explore-activities-routing.constant';
import { IMPACT_PROGRAM_ROUTE } from 'app/impact-program/impact-program-routing.constant';
import { REGISTER_ROUTE } from 'app/register/register-routing.constant';
import { EDITOR_ROUTE } from 'app/routing-constants/editor-routing.constant';
import { STUDENT_ROUTE } from 'app/routing-constants/student-routing.constant';
import { LogicalErrorDocumentType } from 'app/shared/constants/logical-error-document-type';
import { STUDENT_QUESTION_ATTEMPT_ROUTE } from 'app/student-question-attempt/student-question-attempt-routing.constant';
import { PageModeEnum } from 'app/teacher/activities/custom/page-mode.enum';
import { TEACHER_ROUTE } from 'app/teacher/routing-constants/teacher-routing.constant';
import { TEACHERS_ROUTE } from 'app/teachers/teachers-routing.constant';

import { ACCESS_ROUTE } from './access/access-routing.constant';
import { BOARD_ADMIN_ROUTE } from './board-admin/board-admin-routing.constant';
import { DEMO_DISTRICTS_ROUTES } from './demo-district/demo-district-routing.constants';
import { EXTERNAL_URL_ROUTE } from './external-url/external-url-routing.constants';
import { PARENT_ROUTE } from './parent/parent-routing.constant';
import { PARENTS_ROUTE } from './parents/parents-routing.constant';
import { REGIONAL_ADMIN_ROUTE } from './regional-admin/regional-admin-routing.constant';
import { REMEDIATION_ROUTE } from './remediation/remediation-routing.constant';
import { ACTIVITY_MARKETING_ROUTE } from './routing-constants/activity-marketing-routing.constant';
import { ADMIN_ROUTE } from './routing-constants/admin-routing.constant';
import { CS_ADMIN_ROUTE } from './routing-constants/cs-admin-routing.constant';
import { LAUNCH_ROUTE } from './routing-constants/launch-routing.constants';
import { LEADERSHIP_SERIES_ROUTE } from './routing-constants/leadership-routing.constant';
import { MATH_LEADERS_ROUTE } from './routing-constants/math-leaders-routing.constant';
import { RETURN_TO_LEARN_ROUTE } from './routing-constants/return-to-learn-routing.constant';
import { SHOP_ROUTE } from './routing-constants/shop-routing.constant';
import { SCHOOL_ADMIN_ROUTE } from './school-admin/school-admin-routing.constant';
import { SETTINGS_ROUTE } from './settings/settings-routing.constant';
import { VerifyEmailParams } from './shared/enums/verify-email-params.enum';
import { UNSUBSCRIBE_ROUTE } from './unsubscribe/unsubscribe-routing.constants';
import { UNSUBSCRIBE_GROUP_ROUTE } from './unsubscribegroup/unsubscribegroup-routing.constants';

// NOTE: name should be lowercase as lowercase serializer is used.
export const APP_ROUTE = {
  ACCESS: ACCESS_ROUTE,
  ADMIN: ADMIN_ROUTE,
  IMPACT_PROGRAM: IMPACT_PROGRAM_ROUTE,
  DEFAULT: '',
  BOARD_ADMIN: BOARD_ADMIN_ROUTE,
  CONFERENCE: {
    name: 'conference/:collectionId',
    route(collectionId: string): string[] {
      return ['conference', collectionId];
    },

    HOME: {
      name: 'home/authenticated',
      route(collectionId: string): string[] {
        return [...APP_ROUTE.CONFERENCE.route(collectionId), 'home', 'authenticated'];
      }
    },
    PRIZERS: {
      name: 'prizes',
      route(collectionId: string): string[] {
        return [...APP_ROUTE.CONFERENCE.route(collectionId), this.name];
      }
    },
    WEBINAR: {
      name: 'webinar',
      route(collectionId: string): string[] {
        return [...APP_ROUTE.CONFERENCE.route(collectionId), this.name];
      },
      CONTENT: {
        name: ':contentId',
        route(collectionId: string, contentId: string): string[] {
          return [...APP_ROUTE.CONFERENCE.WEBINAR.route(collectionId), contentId];
        }
      }
    },
    WEBINAR_LOAD: {
      name: 'webinarload',
      route(collectionId: string): string[] {
        return [...APP_ROUTE.CONFERENCE.route(collectionId), this.name];
      },
      CONTENT: {
        name: ':contentId',
        route(collectionId: string, contentId: string): string[] {
          return [...APP_ROUTE.CONFERENCE.WEBINAR_LOAD.route(collectionId), contentId];
        }
      }
    }
  },
  CS_ADMIN: CS_ADMIN_ROUTE,
  EDITOR: EDITOR_ROUTE,
  EXPLORE_ACTIVITIES: EXPLORE_ACTIVITIES_ROUTE,
  EXTERNAL_URL: EXTERNAL_URL_ROUTE,
  LAUNCH: LAUNCH_ROUTE,
  LEADERSHIP_SERIES: LEADERSHIP_SERIES_ROUTE,
  LOGIN: {
    name: 'login',
    route(): string[] {
      return ['/' + this.name];
    },
    queryParams(userRole: string, region: string): Params {
      return {
        UserRole: encodeURI(userRole),
        Region: encodeURI(region)
      };
    },
    queryLocaleParam(localeId: string): Params {
      return {
        LocaleId: encodeURI(localeId)
      };
    },
    queryRedirectParam(redirectedFromState: string): Params {
      return {
        RedirectedFromState: encodeURI(redirectedFromState)
      };
    }
  },
  LOGINTOKEN: {
    name: 'logintoken',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  MATH_LEADERS: MATH_LEADERS_ROUTE,
  PARENT_ADMIN: {
    name: 'parentadmin',
    route(): string[] {
      return ['/' + this.name];
    },
    STATISTICS: {
      name: 'statistics',
      route(): string[] {
        return [...APP_ROUTE.PARENT_ADMIN.route(), this.name];
      }
    }
  },
  PARENTS: PARENTS_ROUTE,
  RETURN_TO_LEARN: RETURN_TO_LEARN_ROUTE,
  RFA: {
    name: 'rfa',
    route(): string[] {
      return ['/' + this.name];
    },
    queryParams(route: string, url: string): { from: string; url: string } {
      return {
        from: encodeURI(url),
        url: encodeURI(route)
      };
    }
  },
  SCHOOL_ADMIN: SCHOOL_ADMIN_ROUTE,
  SETTINGS: SETTINGS_ROUTE,
  SHOP: SHOP_ROUTE,
  STUDENT: STUDENT_ROUTE,
  STUDENT_DEFAULT: {
    name: 'student',
    route(): string[] {
      return ['/' + this.name];
    },
    queryParams(classId: string): { classId: string } {
      return {
        classId: encodeURI(classId)
      };
    }
  },
  TEACHER: TEACHER_ROUTE,
  TEACHERS: TEACHERS_ROUTE,
  TEACHING_SUPPORTS: {
    name: 'teachingsupports',
    route(): string[] {
      return ['/' + this.name];
    },
    REMEDIATION: {
      DOCUMENT: {
        name: ':documentType',
        queryParams(classId: string, localeId?: string): Params {
          const params: Params = { classId: encodeURI(classId) };

          if (localeId) {
            params.localeId = encodeURI(localeId);
          }

          return params;
        },
        route(logicalErrorId: string, documentType: LogicalErrorDocumentType): string[] {
          return ['/teachingsupports', 'remediation', logicalErrorId, documentType];
        }
      },
      name: 'remediation/:logicalErrorId',
      route(logicalErrorId: string): string[] {
        return ['/teachingsupports', 'remediation', logicalErrorId];
      }
    }
  },
  TRANSLATIONS: {
    name: 'settings',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  QUESTION_PREVIEW: {
    name: 'previewquestions',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  STUDENT_QUESTION_ATTEMPT_ROUTE: STUDENT_QUESTION_ATTEMPT_ROUTE,
  PARENT: PARENT_ROUTE,
  REGISTER: REGISTER_ROUTE,
  NOVA_GAME_OR: {
    name: 'khg/actiongame.pixi',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  NOVA_GAME_ZR: {
    name: 'khg/actiongame.pixi',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  NOVA_GAME_TF: {
    name: 'khg/terraforming/index.html',
    route(): string[] {
      return [`/${this.name}`];
    }
  },
  NOVA_GAME_ARCADE: {
    name: 'khg/:arcadeGameId',
    route(gameId: string): string[] {
      return [`/khg`, gameId];
    }
  },
  AR: AR_ROUTE,
  REGIONAL_ADMIN: REGIONAL_ADMIN_ROUTE,
  FOLDER: {
    name: 'folder/:pageMode/:sharedFolderId/:folderId',
    route(sharedFolder: string, folderId: string, pageMode: PageModeEnum = PageModeEnum.Default): string[] {
      return ['/folder', pageMode, sharedFolder, folderId];
    }
  },
  REMEDIATION: REMEDIATION_ROUTE,
  ACTIVITY: {
    name: 'activity/:publicunitid',
    route(publicUnitId: string): string[] {
      return ['/activity', publicUnitId];
    }
  },
  PAGE_NOT_FOUND: {
    name: 'page-not-found',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  FORGOT_LOGIN: {
    name: 'forgotlogin',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  VERIFY_EMAIL: {
    name: 'emailverify/:' + VerifyEmailParams.Code,
    route(code: string): string[] {
      return ['/' + 'emailverify' + code];
    }
  },
  UNSUBSCRIBE: UNSUBSCRIBE_ROUTE,
  UNSUBSCRIBEGROUP: UNSUBSCRIBE_GROUP_ROUTE,
  ACTIVITY_MARKETING: ACTIVITY_MARKETING_ROUTE,
  DEMO_DISTRICT: DEMO_DISTRICTS_ROUTES,
  FORBIDDEN: {
    name: 'forbidden',
    route(): string[] {
      return ['/' + this.name];
    }
  },
  CALENDARS: {
    name: 'calendars',
    route(): string[] {
      return ['/' + this.name];
    }
  }
};
